import React, { useEffect, useState, useCallback } from 'react';

const PRIORITIES = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'];

const PRIORITY_COLORS = {
  CRITICAL: '#fde8e8',
  HIGH: '#fff3e0',
  MEDIUM: '#fff9e6',
  LOW: '#e8f5e9',
};

const DEFAULT_RULES = PRIORITIES.map((p) => ({
  priority: p,
  response_time_mins: '',
  resolution_time_mins: '',
  escalation_l1_mins: '',
  escalation_l2_mins: '',
  escalation_l3_mins: '',
}));

const COLUMN_HELP = [
  { label: 'Response Time', desc: 'Max time from work order creation to assignment (minutes).' },
  { label: 'Resolution Time', desc: 'Max time from assignment to resolution (minutes).' },
  { label: 'Escalation L1', desc: 'Time after which the work order escalates to Level-1 supervisor (minutes).' },
  { label: 'Escalation L2', desc: 'Time after which the work order escalates to Level-2 manager (minutes).' },
  { label: 'Escalation L3', desc: 'Time after which the work order escalates to Level-3 senior management (minutes).' },
];

function MaintSlaConfig({ token, selectedProperty }) {
  const [rules, setRules] = useState(DEFAULT_RULES);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const headers = useCallback(() => ({
    Authorization: 'Bearer ' + token,
    'x-chain-id': selectedProperty?.chain_id,
    'x-property-id': selectedProperty?.id,
  }), [token, selectedProperty]);

  const loadConfig = useCallback(async () => {
    if (!token || !selectedProperty?.id) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/v1/maintenance/sla-config', { headers: headers() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || 'Failed to load SLA config');
        setLoading(false);
        return;
      }
      const fetched = Array.isArray(data.rules) ? data.rules : [];
      if (fetched.length) {
        // Merge fetched into default order so all 4 priorities always show
        const merged = PRIORITIES.map((p) => {
          const match = fetched.find((r) => r.priority === p);
          return match
            ? {
                priority: p,
                response_time_mins: match.response_time_mins ?? '',
                resolution_time_mins: match.resolution_time_mins ?? '',
                escalation_l1_mins: match.escalation_l1_mins ?? '',
                escalation_l2_mins: match.escalation_l2_mins ?? '',
                escalation_l3_mins: match.escalation_l3_mins ?? '',
              }
            : { ...DEFAULT_RULES.find((d) => d.priority === p) };
        });
        setRules(merged);
      } else {
        setRules(DEFAULT_RULES.map((r) => ({ ...r })));
      }
    } catch (err) {
      setError(err?.message || 'Network error');
    } finally {
      setLoading(false);
    }
  }, [token, selectedProperty, headers]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const handleChange = (idx, field, value) => {
    setRules((prev) => {
      const next = prev.map((r) => ({ ...r }));
      next[idx][field] = value === '' ? '' : Number(value);
      return next;
    });
    setSuccess('');
  };

  const handleSave = async () => {
    setError('');
    setSuccess('');
    setSaving(true);
    try {
      const res = await fetch('/api/v1/maintenance/sla-config', {
        method: 'POST',
        headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ rules }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || 'Failed to save SLA config');
      } else {
        setSuccess('SLA configuration saved successfully.');
      }
    } catch (err) {
      setError(err?.message || 'Network error');
    } finally {
      setSaving(false);
    }
  };

  const FIELDS = [
    { key: 'response_time_mins', label: 'Response Time (mins)' },
    { key: 'resolution_time_mins', label: 'Resolution Time (mins)' },
    { key: 'escalation_l1_mins', label: 'Escalation L1 (mins)' },
    { key: 'escalation_l2_mins', label: 'Escalation L2 (mins)' },
    { key: 'escalation_l3_mins', label: 'Escalation L3 (mins)' },
  ];

  return (
    <div style={{ padding: 24, maxWidth: 960, margin: '0 auto' }}>
      <h2 style={{ marginBottom: 4 }}>SLA Configuration</h2>
      <p style={{ color: '#666', fontSize: 14, marginBottom: 16 }}>
        Define response, resolution, and escalation time limits for each work-order priority level.
      </p>

      {error && (
        <div style={{ background: '#fde8e8', color: '#b71c1c', padding: '8px 12px', borderRadius: 4, marginBottom: 12, fontSize: 14 }}>
          {error}
        </div>
      )}
      {success && (
        <div style={{ background: '#e8f5e9', color: '#2e7d32', padding: '8px 12px', borderRadius: 4, marginBottom: 12, fontSize: 14 }}>
          {success}
        </div>
      )}

      {loading ? (
        <p>Loading...</p>
      ) : (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ background: '#f5f5f5' }}>
                  <th style={thStyle}>Priority</th>
                  {FIELDS.map((f) => (
                    <th key={f.key} style={thStyle}>{f.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rules.map((rule, idx) => (
                  <tr key={rule.priority} style={{ background: PRIORITY_COLORS[rule.priority] || '#fff' }}>
                    <td style={{ ...tdStyle, fontWeight: 600 }}>{rule.priority}</td>
                    {FIELDS.map((f) => (
                      <td key={f.key} style={tdStyle}>
                        <input
                          type="number"
                          min="0"
                          value={rule[f.key]}
                          onChange={(e) => handleChange(idx, f.key, e.target.value)}
                          style={inputStyle}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ marginTop: 16 }}>
            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                padding: '8px 24px',
                background: saving ? '#999' : '#1976d2',
                color: '#fff',
                border: 'none',
                borderRadius: 4,
                cursor: saving ? 'not-allowed' : 'pointer',
                fontSize: 14,
                fontWeight: 600,
              }}
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>

          <div style={{ marginTop: 24, padding: 16, background: '#f9f9f9', borderRadius: 6, border: '1px solid #e0e0e0' }}>
            <h4 style={{ margin: '0 0 8px' }}>Column Reference</h4>
            <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13, color: '#555', lineHeight: 1.8 }}>
              {COLUMN_HELP.map((c) => (
                <li key={c.label}>
                  <strong>{c.label}:</strong> {c.desc}
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </div>
  );
}

const thStyle = {
  padding: '10px 8px',
  textAlign: 'left',
  borderBottom: '2px solid #ddd',
  whiteSpace: 'nowrap',
};

const tdStyle = {
  padding: '8px',
  borderBottom: '1px solid #e0e0e0',
};

const inputStyle = {
  width: '100%',
  padding: '6px 8px',
  border: '1px solid #ccc',
  borderRadius: 4,
  fontSize: 14,
  boxSizing: 'border-box',
};

export default MaintSlaConfig;
